import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AlphabetSorting } from '@hermes/design-system'
import {
  keywordContainer,
  keywordHelperContainer,
  keywordHelperText,
  keywordHelperTextUnderlined,
  keywordText
} from '../areasOfExpertise.css'
import { ProfileContext } from '../../../../providers/AppData/Profile'
import { TransformedKeywordType } from '../index'
import { AppDataContext } from '../../../../providers/AppData'
import { ProfileKeyword } from '../../../../types'
import { contentContainer, alphabetSortingContainer, scrollButtonContainer } from './areasOfExpertise.css'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { SearchReviewsContext } from '../../../../providers/SearchReviews'
import { AreasSortingTabs, LAST_KEYWORD_IN_AREA_OF_EXPERTISE } from '../../../../constants/profile'
import SortingRatingBar from '../SortingRatingBar'
import ScrollButton, { ScrollButtonDirection } from '../../../../components/ScrollButton'
import { useIsInViewport } from '../../../../hooks/useIsInViewport'
import { getKeywords } from '../utils/getKeywords'
import Modal from '../../../../components/Modal'
import RatingBar from '../../../RatingBarForArea'

type AreasOfExpertiseModalProps = {
  isOpen: boolean
  onClose: () => void
  summaryCountReviews: number
  summaryCountEndorsements: number
  sortingOptionToHide?: AreasSortingTabs | null
}

type KeywordWithLetter = TransformedKeywordType & { letter?: string }

const addFirstLetterToGroups = (keywords: TransformedKeywordType[]): KeywordWithLetter[] => {
  const sorted = [...keywords].sort((a, b) => a.name.localeCompare(b.name))
  return sorted.map((kw, i, arr) => {
    const letter = kw.name.charAt(0).toUpperCase()
    const isNewGroup = i === 0 || letter !== arr[i - 1].name.charAt(0).toUpperCase()

    return isNewGroup ? { ...kw, letter, id: kw.name } : { ...kw }
  })
}

const AreasOfExpertiseModal: FC<AreasOfExpertiseModalProps> = ({
  isOpen,
  onClose,
  summaryCountReviews,
  summaryCountEndorsements,
  sortingOptionToHide
}) => {
  const { specialist, practice } = useContext(ProfileContext)
  const { language } = useContext(AppDataContext)
  const [hidedSortOption, setHidedSortOption] = useState<string | null>(null)
  const [transformedKeywords, setTransformedKeywords] = useState<TransformedKeywordType[]>([])
  const [shouldShowScrollButton, setShouldShowScrollButton] = useState(false)
  const translate = useTranslateMessage()

  const { changeKeyword } = useContext(SearchReviewsContext)

  const keywords = useMemo(() => getKeywords(specialist, practice), [specialist, practice])

  const initialKeywordsProcessing = useCallback(() => {
    const transformed = keywords.map((keyword: ProfileKeyword) => ({
      name: keyword.name[language],
      countInReviews: keyword?.countInReviews || 0,
      countInPeerRecommendations: keyword?.countInPeerRecommendations || 0
    }))
    setTransformedKeywords(addFirstLetterToGroups(transformed))
  }, [keywords, language])

  useEffect(() => {
    initialKeywordsProcessing()
  }, [initialKeywordsProcessing])

  const handleAlphabetSort = () => {
    setHidedSortOption(null)
    setTransformedKeywords((prev) => prev.sort((a, b) => a.name.localeCompare(b.name)))
  }

  const handleReviewsSort = () => {
    setHidedSortOption(AreasSortingTabs.ENDORSEMENTS)
    setTransformedKeywords((prev) => prev.sort((a, b) => b.countInReviews - a.countInReviews))
  }

  const handleEndorsementsSort = () => {
    setHidedSortOption(AreasSortingTabs.REVIEWS)
    setTransformedKeywords((prev) => prev.sort((a, b) => b.countInPeerRecommendations - a.countInPeerRecommendations))
  }

  const initialSortingFunction = useMemo(
    () => ({
      [AreasSortingTabs.ENDORSEMENTS]: handleReviewsSort,
      [AreasSortingTabs.REVIEWS]: handleEndorsementsSort,
      [AreasSortingTabs.ALPHABET]: handleAlphabetSort
    }),
    []
  )

  useEffect(() => {
    if (sortingOptionToHide !== undefined) {
      setHidedSortOption(sortingOptionToHide)
      initialKeywordsProcessing()
      initialSortingFunction[sortingOptionToHide || AreasSortingTabs.ALPHABET]()
    }
  }, [sortingOptionToHide, initialSortingFunction, initialKeywordsProcessing])

  const currentRatingNumber = useCallback(
    (number: number) => {
      if (hidedSortOption === AreasSortingTabs.REVIEWS) {
        return 0
      }
      return number
    },
    [hidedSortOption]
  )

  const currentEndorsementsNumber = useCallback(
    (number: number) => {
      if (hidedSortOption === AreasSortingTabs.ENDORSEMENTS) {
        return 0
      }
      return number
    },
    [hidedSortOption]
  )

  const explanationText = useMemo(() => {
    if (hidedSortOption === null) {
      return null
    }
    if (hidedSortOption === AreasSortingTabs.REVIEWS) {
      return translate('profile.areas-of-expertise.explanation-text.endorsements')
    }

    return translate('profile.areas-of-expertise.explanation-text.reviews')
  }, [hidedSortOption, translate])

  const handleKeywordClick = (keyword: TransformedKeywordType) => {
    changeKeyword(keyword.name)
  }

  const handleScrollButtonClick = useCallback(() => {
    const element = document.getElementById(LAST_KEYWORD_IN_AREA_OF_EXPERTISE) as HTMLElement

    if (!element) {
      return
    }

    element.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const isLastElementVisible: boolean = useIsInViewport({ elementId: LAST_KEYWORD_IN_AREA_OF_EXPERTISE })

  useEffect(() => {
    setShouldShowScrollButton(!isLastElementVisible)
  }, [isLastElementVisible])

  const getShouldShowRatingBar = useCallback(
    (keyword: TransformedKeywordType) =>
      !!currentRatingNumber(keyword.countInReviews) || !!currentEndorsementsNumber(keyword.countInPeerRecommendations),
    [currentRatingNumber, currentEndorsementsNumber]
  )

  const alphabetSortingList = useMemo(() => {
    return transformedKeywords.map(({ id, name, letter }) => ({
      id: id || '',
      label: name || '',
      letter: letter || '',
    }))
  }, [transformedKeywords])

  return (
    <Modal isOpen={isOpen} onClose={onClose} titleText={translate('profile.areas-of-expertise.all')}>
      <div className={contentContainer}>
        {summaryCountReviews && summaryCountEndorsements ? (
          <SortingRatingBar
            handleAlphabetSort={handleAlphabetSort}
            handleReviewsSort={handleReviewsSort}
            handleEndorsementsSort={handleEndorsementsSort}
            reviewsCount={summaryCountReviews}
            endorsementsCount={summaryCountEndorsements}
            showAlphabetExceptAll
            namingOptions={{
              alphabet: {
                az: translate('global.az'),
                all: translate('global.all')
              },
              reviews: translate('global.reviews'),
              endorsements: translate('global.endorsements')
            }}
            sortingOptionToHide={sortingOptionToHide}
          />
        ) : null}
        {explanationText ? (
          <div className={keywordHelperContainer}>
            {summaryCountReviews ? (
              <span className={keywordHelperTextUnderlined}>
                {translate('profile.areas-of-expertise.patient-reviews', { number: summaryCountReviews })}
              </span>
            ) : null}
            <span className={keywordHelperText}>{explanationText}</span>
          </div>
        ) : null}
        {transformedKeywords.map((keyword, index) => {
          const isLastElement = index === transformedKeywords.length - 1
          const id = isLastElement ? LAST_KEYWORD_IN_AREA_OF_EXPERTISE : keyword.id
          return (
            <div key={keyword.name} id={id} className={keywordContainer}>
              <span className={keywordText}>{keyword.name}</span>
              {getShouldShowRatingBar(keyword) ? (
                <RatingBar
                  currentRating={currentRatingNumber(keyword.countInReviews)}
                  currentEndorsement={currentEndorsementsNumber(keyword.countInPeerRecommendations)}
                  onClick={() => handleKeywordClick(keyword)}
                />
              ) : null}
            </div>
          )
        })}
        <AlphabetSorting sortingList={alphabetSortingList} className={alphabetSortingContainer} />
        {shouldShowScrollButton ? (
          <ScrollButton
            className={scrollButtonContainer}
            onClick={handleScrollButtonClick}
            direction={ScrollButtonDirection.DOWN}
          />
        ) : null}
      </div>
    </Modal>
  )
}

export default AreasOfExpertiseModal

